// Shared formatting helpers for Zasso note output.
//
// Two markdown builders mirror the Okoshi split:
//   - buildMarkdownForVault   — BODY ONLY; VaultRPC prepends frontmatter.
//   - buildMarkdownForDownload — frontmatter + body; self-contained .md.
//
// spec §2.10 shows the reference shape: `# ザッソウ YYYY-MM-DD HH:MM`
// with alternating `## ユーザー` / `## コーチ` blocks.

export interface ZassoTurn {
  role: "user" | "assistant";
  text: string;
}

/** Format a Date as `YYYY-MM-DD-HHMM` in JST (UTC+9 arithmetic). */
export function formatJstStamp(when: Date | number = Date.now()): string {
  const d = typeof when === "number" ? new Date(when) : when;
  const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const yyyy = jst.getUTCFullYear();
  const mm = String(jst.getUTCMonth() + 1).padStart(2, "0");
  const dd = String(jst.getUTCDate()).padStart(2, "0");
  const hh = String(jst.getUTCHours()).padStart(2, "0");
  const mi = String(jst.getUTCMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}-${hh}${mi}`;
}

export function splitStamp(stamp: string) {
  return {
    date: stamp.slice(0, 10),
    hhmm: `${stamp.slice(11, 13)}:${stamp.slice(13, 15)}`,
  };
}

/** Render duration as "Nm" (floor-minutes) for the frontmatter field. */
export function formatDurationMinutes(sec: number): string {
  const minutes = Math.max(0, Math.floor(sec / 60));
  return `${minutes}m`;
}

/** Short "Nm SSs" label — shared between idle footer and stopped summary. */
export function formatDuration(sec: number): string {
  const total = Math.max(0, Math.floor(sec));
  const minutes = Math.floor(total / 60);
  const seconds = total % 60;
  return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
}

export interface ZassoMarkdownMeta {
  title: string;
  stamp: string;
  dateTimeIso: string;
}

function buildBody(turns: readonly ZassoTurn[], when: Date): {
  body: string;
  meta: ZassoMarkdownMeta;
} {
  const stamp = formatJstStamp(when);
  const { date, hhmm } = splitStamp(stamp);
  const title = `ザッソウ ${date} ${hhmm}`;

  const sections = turns
    .filter((t) => t.text.trim().length > 0)
    .map((t) => {
      const header = t.role === "user" ? "## ユーザー" : "## コーチ";
      return `${header}\n\n${t.text.trim()}`;
    });
  const bodyText = sections.length > 0 ? sections.join("\n\n") : "(空)";
  const body = `# ${title}\n\n${bodyText}\n`;

  return {
    body,
    meta: { title, stamp, dateTimeIso: when.toISOString() },
  };
}

/** Body-only Markdown for `/api/vault/save`. */
export function buildMarkdownForVault(
  turns: readonly ZassoTurn[],
  when: Date = new Date(),
): { markdown: string; meta: ZassoMarkdownMeta } {
  const { body, meta } = buildBody(turns, when);
  return { markdown: body, meta };
}

/** Self-contained Markdown for the "DL · .md" action. */
export function buildMarkdownForDownload(
  turns: readonly ZassoTurn[],
  durationSec: number,
  when: Date = new Date(),
): { markdown: string; meta: ZassoMarkdownMeta } {
  const { body, meta } = buildBody(turns, when);
  const { date } = splitStamp(meta.stamp);
  const markdown =
    `---\ntype: zasso-session\ndate: ${date}\nsource: koe\nduration: ${formatDurationMinutes(durationSec)}\n---\n\n${body}`;
  return { markdown, meta };
}

/** For the "NEXT" path preview on the idle screen. */
export function nextZassoPathPreview(now: Date = new Date()): string {
  return `inbox/zasso/${formatJstStamp(now)}.md`;
}
